import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, Calendar, MessageCircle, Clock, Shield, Sparkles } from 'lucide-react';
import { coachingPackages, coachingPageCopy, siteConfig } from '../data/mock';
import { SEO, pageSEO } from '../components/SEO';

export const CoachingPage = () => {
  const handleBookPackage = (packageItem) => {
    // Opens Calendly with package info, then user completes payment separately
    window.open(`${siteConfig.calendlyUrl}/${packageItem.calendlyEvent}`, '_blank');
  };

  return (
    <div className="bg-[var(--color-charcoal)]">
      <SEO {...pageSEO.coaching} path="/coaching" />
      {/* SECTION 1 — OPENING */}
      <section className="relative min-h-[80vh] flex items-center pt-32 pb-24">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1604785800517-e972a4c16f2b?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--color-charcoal)] via-[var(--color-charcoal)]/90 to-[var(--color-charcoal)]/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-charcoal)] via-transparent to-[var(--color-charcoal)]/50" />
        </div>
        
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-6 block">
              Work With Me
            </span>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mb-4 leading-[1.05]">
              {coachingPageCopy.opening.headline}
            </h1>
            <p className="font-serif text-2xl md:text-3xl text-[var(--color-bottle-green-light)] mb-8">
              {coachingPageCopy.opening.subheadline}
            </p>
            <p className="text-xl text-[var(--color-cream)]/80 leading-relaxed max-w-3xl mb-6">
              {coachingPageCopy.opening.intro}
            </p>
            <p className="text-lg text-[var(--color-cream)]/60 leading-relaxed max-w-3xl">
              {coachingPageCopy.opening.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 — WHO THIS IS FOR */}
      <section className="py-24 lg:py-32 bg-[var(--color-charcoal-light)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
              Is This For You?
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-6">
              {coachingPageCopy.whoIsThisFor.headline}
            </h2>
            <p className="text-xl text-[var(--color-cream)]/70 max-w-2xl mx-auto">
              {coachingPageCopy.whoIsThisFor.description}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* This IS For */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 lg:p-10 bg-[var(--color-bottle-green)]/10 rounded-lg border border-[var(--color-bottle-green)]/20"
            >
              <h3 className="font-serif text-2xl mb-8 text-[var(--color-cream)]">
                This Work Is For You If...
              </h3>
              <ul className="space-y-5">
                {coachingPageCopy.whoIsThisFor.traits.map((trait, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-[var(--color-bottle-green)]/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-[var(--color-bottle-green-light)]" />
                    </div>
                    <span className="text-[var(--color-cream)]/75 leading-relaxed">{trait}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* This Is NOT */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 lg:p-10 bg-[var(--color-charcoal)] rounded-lg border border-[var(--color-dark-purple)]/30"
            >
              <h3 className="font-serif text-2xl mb-8 text-[var(--color-cream)]">
                Please Know...
              </h3>
              <ul className="space-y-5">
                {coachingPageCopy.whoIsThisFor.notFor.map((item, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-[var(--color-deep-red)]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <X className="w-3.5 h-3.5 text-[var(--color-deep-red-light)]" />
                    </div>
                    <span className="text-[var(--color-cream)]/60 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — HOW SESSIONS WORK */}
      <section className="py-24 lg:py-32">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
              The Process
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-6">
              {coachingPageCopy.howSessionsWork.headline}
            </h2>
            <p className="text-lg text-[var(--color-cream)]/70 max-w-2xl mx-auto">
              {coachingPageCopy.howSessionsWork.description}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coachingPageCopy.howSessionsWork.elements.map((element, index) => {
              const icons = [Clock, MessageCircle, Sparkles, Shield, Shield];
              const Icon = icons[index] || Shield;
              const colors = [
                'bottle-green',
                'midnight-blue',
                'dark-purple',
                'deep-red',
                'bottle-green'
              ];
              const color = colors[index];
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-6 bg-[var(--color-charcoal-light)] rounded-lg border-l-2 ${
                    color === 'bottle-green'
                      ? 'border-[var(--color-bottle-green)]'
                      : color === 'midnight-blue'
                      ? 'border-[var(--color-midnight-blue)]'
                      : color === 'dark-purple'
                      ? 'border-[var(--color-dark-purple)]'
                      : 'border-[var(--color-deep-red)]'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-4 ${
                    color === 'bottle-green'
                      ? 'bg-[var(--color-bottle-green)]/20'
                      : color === 'midnight-blue'
                      ? 'bg-[var(--color-midnight-blue)]/20'
                      : color === 'dark-purple'
                      ? 'bg-[var(--color-dark-purple)]/30'
                      : 'bg-[var(--color-deep-red)]/20'
                  }`}>
                    <Icon className="w-5 h-5 text-[var(--color-cream)]/70" />
                  </div>
                  <h3 className="font-serif text-lg mb-2 text-[var(--color-cream)]">
                    {element.title}
                  </h3>
                  <p className="text-sm text-[var(--color-cream)]/60 leading-relaxed">
                    {element.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 4 — COACHING PACKAGES */}
      <section className="py-24 lg:py-32 bg-[var(--color-charcoal-light)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
              Packages
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-6">
              Choose Your Container
            </h2>
            <p className="text-lg text-[var(--color-cream)]/70 max-w-2xl mx-auto">
              Each package is designed for depth and integration. Choose the container that honours where you are in your journey.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coachingPackages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className={`relative p-8 rounded-lg transition-all duration-300 ${
                  index === 1
                    ? 'bg-[var(--color-bottle-green)]/15 border border-[var(--color-bottle-green)]/40'
                    : 'bg-[var(--color-charcoal)] border border-[var(--color-dark-purple)]/30'
                }`}
              >
                {index === 1 && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 bg-[var(--color-bottle-green)] text-[var(--color-cream)] text-xs tracking-[0.15em] uppercase rounded-full">
                      Most Chosen
                    </span>
                  </div>
                )}
                
                <div className="mb-6">
                  <h3 className="font-serif text-2xl mb-1">{pkg.name}</h3>
                  <p className="text-[var(--color-gold)] text-sm">{pkg.sessions}</p>
                </div>
                
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-sm text-[var(--color-cream)]/60">{pkg.currency}</span>
                  <span className="text-4xl font-serif text-[var(--color-cream)]">
                    {pkg.price}
                  </span>
                </div>
                
                <p className="text-[var(--color-cream)]/60 mb-8 leading-relaxed text-sm">
                  {pkg.description}
                </p>
                
                <div className="space-y-3 mb-8">
                  {pkg.includes.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-[var(--color-gold)] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[var(--color-cream)]/70">{item}</span>
                    </div>
                  ))}
                </div>
                
                <button
                  onClick={() => handleBookPackage(pkg)}
                  className={`w-full py-4 rounded-full font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                    index === 1
                      ? 'bg-[var(--color-bottle-green)] text-[var(--color-cream)] hover:bg-[var(--color-bottle-green-light)]'
                      : 'border border-[var(--color-gold)] text-[var(--color-gold)] hover:bg-[var(--color-gold)] hover:text-[var(--color-charcoal)]'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  Book This Package
                </button>
              </motion.div>
            ))}
          </div>

          {/* Payment Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 max-w-2xl mx-auto text-center"
          >
            <p className="text-sm text-[var(--color-cream)]/50 leading-relaxed">
              After scheduling your session, you'll receive a secure payment link. Payment confirms your booking. All sessions are conducted via video call.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 5 — BOOKING PROCESS */}
      <section className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
              How It Works
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-6">
              The Booking Process
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                step: '01',
                title: 'Choose Your Package',
                description: 'Select the container that feels right for where you are in your journey.'
              },
              {
                step: '02',
                title: 'Schedule Your First Session',
                description: 'You\'ll be directed to my calendar to select a time that works for you.'
              },
              {
                step: '03',
                title: 'Complete Payment',
                description: 'After scheduling, you\'ll receive a secure payment link to confirm your booking.'
              },
              {
                step: '04',
                title: 'Receive Confirmation',
                description: 'Once payment is complete, you\'ll receive session details and preparation notes within 24 hours.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-6 p-6 bg-[var(--color-charcoal-light)] rounded-lg"
              >
                <div className="flex-shrink-0">
                  <span className="font-serif text-2xl text-[var(--color-gold)]">{item.step}</span>
                </div>
                <div>
                  <h3 className="font-serif text-xl mb-2">{item.title}</h3>
                  <p className="text-[var(--color-cream)]/60">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ethical Note */}
      <section className="py-16 bg-[var(--color-dark-purple)]/15">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-[var(--color-cream)]/50 text-sm leading-relaxed">
            <strong className="text-[var(--color-cream)]/70">A Note on Boundaries:</strong> Coaching is a space for growth and inquiry. It is not therapy, and it is not a substitute for mental health treatment. If you are experiencing a mental health crisis or require clinical support, please seek appropriate professional care. This work is most effective when approached with openness, honesty, and a genuine commitment to self-examination.
          </p>
        </div>
      </section>
    </div>
  );
};
